import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Target, Trash2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth, errorMessage } from "@/lib/auth";
import AppPreview from "@/components/AppPreview";
import { GAMES, GOALS, INTENTS, ranksFor } from "@/content/onboarding";
import "@/auth.css";

const STEP_ICONS = { Target, Users };
const TOTAL = 3;

// Redimensionne dans le navigateur : l'API ne reçoit jamais une photo de 8 Mo.
function resizeToDataUrl(file, size = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function Onboarding() {
  const { user, loading, updateProfile, uploadAvatar, removeAvatar } = useAuth();
  const nav = useNavigate();
  const fileInput = useRef(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", username: "", newsletter: false,
    game: "", rank: "", goal: "", role_intent: "player",
  });

  useEffect(() => {
    if (!user) return;
    setForm(f => ({
      ...f,
      first_name: user.first_name || "", last_name: user.last_name || "",
      username: user.username || "", newsletter: !!user.newsletter,
      game: user.game || "", rank: user.rank || "", goal: user.goal || "",
      role_intent: user.role_intent || "player",
    }));
  }, [user]);

  if (loading) return <div className="route-loading" aria-busy="true" />;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.onboarded) return <Navigate to="/dashboard" replace />;

  const set = (k, v) => setForm(f => ({ ...f, ...(k === "game" ? { game: v, rank: "" } : { [k]: v }) }));
  const initials = ((form.first_name[0] || "") + (form.last_name[0] || "")).toUpperCase() || (user.email[0] || "P").toUpperCase();

  const pickFile = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Formats acceptés : JPEG, PNG ou WebP."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde : 10 Mo maximum."); return; }
    setUploading(true);
    try {
      await uploadAvatar(await resizeToDataUrl(file));
      toast.success("Photo mise à jour.");
    } catch (err) {
      toast.error(errorMessage(err, "Le téléversement a échoué."));
    } finally { setUploading(false); }
  };

  const save = async (patch, next) => {
    setBusy(true);
    try {
      await updateProfile(patch);
      if (next === "done") { toast.success("Bienvenue sur Perform."); nav("/dashboard", { replace: true }); }
      else setStep(next);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setBusy(false); }
  };

  const submit = e => {
    e.preventDefault();
    if (step === 0) {
      const patch = { first_name: form.first_name, last_name: form.last_name, newsletter: form.newsletter };
      if (form.username && form.username !== user.username) patch.username = form.username;
      return save(patch, 1);
    }
    if (step === 1) return save({ game: form.game, rank: form.rank }, 2);
    return save({ goal: form.goal, role_intent: form.role_intent, onboarded: true }, "done");
  };

  const skip = () => (step === TOTAL - 1 ? save({ onboarded: true }, "done") : setStep(step + 1));

  return (
    <div className="onb">
      <div className="onb-left">
        {step > 0
          ? <button type="button" className="onb-back" onClick={() => setStep(step - 1)} aria-label="Étape précédente" data-testid="onb-back"><ArrowLeft size={16} /></button>
          : <div style={{ height: 30, marginBottom: 26 }} />}

        <div className="onb-progress" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={TOTAL}>
          {Array.from({ length: TOTAL }, (_, i) => <i key={i} className={i <= step ? "is-done" : ""} />)}
        </div>

        <form className="onb-form" onSubmit={submit}>
          {step === 0 && (
            <>
              <h1>Faisons connaissance.</h1>
              <p>Ces informations apparaissent sur votre profil et auprès de vos coachs.</p>

              <div className="onb-avatar">
                <div className="onb-avatar-preview">
                  {user.avatar_url
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" />
                    : initials}
                </div>
                <div>
                  <h3>Photo de profil</h3>
                  <div className="onb-avatar-buttons">
                    <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pickFile} data-testid="onb-avatar-input" />
                    <button type="button" className="button button-outline" disabled={uploading} onClick={() => fileInput.current?.click()} data-testid="onb-avatar-upload">
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                      {uploading ? "Envoi…" : "Choisir une image"}
                    </button>
                    {user.avatar_url && (
                      <button type="button" className="button button-outline" onClick={() => removeAvatar()} data-testid="onb-avatar-remove" aria-label="Retirer la photo"><Trash2 size={13} /></button>
                    )}
                  </div>
                  <p className="onb-hint">JPEG, PNG ou WebP. Recadrée en 256 × 256.</p>
                </div>
              </div>

              <div className="onb-row">
                <div className="onb-field">
                  <label htmlFor="onb-first">Prénom</label>
                  <input id="onb-first" data-testid="onb-first-name" value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="Votre prénom" maxLength={60} autoFocus />
                </div>
                <div className="onb-field">
                  <label htmlFor="onb-last">Nom</label>
                  <input id="onb-last" data-testid="onb-last-name" value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="Votre nom" maxLength={60} />
                </div>
              </div>

              <div className="onb-field">
                <label htmlFor="onb-username">Pseudo</label>
                <input id="onb-username" data-testid="onb-username" value={form.username} onChange={e => set("username", e.target.value)} placeholder="votre_pseudo" maxLength={40} />
                <p className="onb-hint">Visible publiquement. Lettres, chiffres, tiret, point et souligné.</p>
              </div>

              <div className="onb-field">
                <label htmlFor="onb-email">Adresse e-mail</label>
                <input id="onb-email" value={user.email} disabled />
              </div>

              <div className="onb-toggle-row">
                <div>
                  <strong>Recevoir les nouveautés</strong>
                  <span>Les nouveautés produit et les conseils de coachs. Une ou deux fois par mois, pas plus.</span>
                </div>
                <button type="button" role="switch" aria-checked={form.newsletter} aria-label="Recevoir les nouveautés" className="onb-switch" data-testid="onb-newsletter" onClick={() => set("newsletter", !form.newsletter)}><i /></button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h1>Sur quoi jouez-vous ?</h1>
              <p>Nous adapterons les séances à ce jeu et à votre niveau.</p>
              <div className="onb-field">
                <label htmlFor="onb-game">Jeu principal</label>
                <select id="onb-game" data-testid="onb-game" value={form.game} onChange={e => set("game", e.target.value)} required>
                  <option value="" disabled>Choisissez un jeu</option>
                  {GAMES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="onb-field">
                <label htmlFor="onb-rank">Niveau actuel</label>
                <select id="onb-rank" data-testid="onb-rank" value={form.rank} onChange={e => set("rank", e.target.value)} disabled={!form.game}>
                  <option value="">{form.game ? "Choisissez votre palier" : "Choisissez d'abord un jeu"}</option>
                  {ranksFor(form.game).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="onb-hint">Modifiable à tout moment depuis votre profil.</p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1>Qu'est-ce qui vous amène ?</h1>
              <p>Dernière étape. Nous adaptons votre espace en conséquence.</p>
              <div className="onb-choices" style={{ marginBottom: 26 }}>
                {INTENTS.map(opt => {
                  const Icon = STEP_ICONS[opt.icon];
                  return (
                    <button key={opt.key} type="button" className="onb-choice" aria-pressed={form.role_intent === opt.key} onClick={() => set("role_intent", opt.key)} data-testid={`onb-intent-${opt.key}`}>
                      <span className="onb-choice-icon"><Icon size={16} /></span>
                      <span><strong>{opt.title}</strong><small>{opt.desc}</small></span>
                      <Check size={16} className="onb-choice-check" />
                    </button>
                  );
                })}
              </div>
              <div className="onb-field">
                <label htmlFor="onb-goal">Votre objectif</label>
                <input id="onb-goal" data-testid="onb-goal" list="onb-goal-suggestions" value={form.goal} onChange={e => set("goal", e.target.value)} placeholder="Par exemple : passer Diamant avant juin" maxLength={200} />
                <datalist id="onb-goal-suggestions">{GOALS.map(g => <option key={g} value={g} />)}</datalist>
                <p className="onb-hint">Il s'affichera en tête de votre tableau de bord.</p>
              </div>
            </>
          )}

          <div className="onb-actions">
            <button type="submit" className="button button-dark" disabled={busy || (step === 1 && !form.game)} data-testid="onb-continue">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {step === TOTAL - 1 ? "Terminer" : "Continuer"}
              {!busy && <ArrowRight size={14} />}
            </button>
            <button type="button" className="onb-skip" onClick={skip} data-testid="onb-skip">Passer cette étape</button>
          </div>

          <div className="onb-footer">
            <span>Perform © {new Date().getFullYear()}</span>
            <Link to="/legal/confidentialite">Confidentialité</Link>
            <Link to="/aide">Support</Link>
          </div>
        </form>
      </div>

      <div className="onb-right"><AppPreview /></div>
    </div>
  );
}
